import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEditorScope } from 'app/state/contexts/EditorScopeContext';
import { documentKeys, useEditorDocumentQuery, type ReferenceDataValue } from 'entities/document';
import { referenceListKey, useReferenceList } from 'entities/reference-data';
import { useCss } from 'entities/style';
import { mapLanguages } from 'app/utils/translations';

export const useEditorDocumentOptional = () => {
  const scope = useEditorScope();
  return useEditorDocumentQuery({
    documentId: scope?.documentId ?? '',
    documentRevisionId: scope?.documentRevisionId,
    useDraft: scope?.useDraft,
    withStyle: scope?.withStyle,
  });
};

export const useEditorDocument = () => {
  const { data, ...query } = useEditorDocumentOptional();
  if (!data) {
    throw new Error('useEditorDocument used outside a loaded Editor, which renders nothing until the document resolves');
  }
  return { ...query, data };
};

export const useEditorReferencesQuery = () => {
  const { data: document } = useEditorDocument();
  const { data, isLoading } = useReferenceList(document, { keepPreviousList: true });
  return { references: data ?? [], isLoading };
};

export const useEditorReferences = () => useEditorReferencesQuery().references;

export const useEditorLanguages = () => {
  const sectionId = useEditorScope()?.sectionId;
  const { data: document } = useEditorDocument();
  return useMemo(() => (document && sectionId ? mapLanguages(document, sectionId) : []), [document, sectionId]);
};

export const useEditorLocale = (sectionSuperId?: string) => {
  const scope = useEditorScope();
  const { sectionId, translationId } = scope ?? {};
  const languages = useEditorLanguages();
  const superId = sectionSuperId ?? translationId ?? sectionId;
  return useMemo(() => languages.find((language) => language.superId === superId)?.locale ?? 'sv-SE', [languages, superId]);
};

export const useSetEditorReferences = () => {
  const queryClient = useQueryClient();
  const { data: document } = useEditorDocument();

  return useCallback(
    (updater: (references: ReferenceDataValue[]) => ReferenceDataValue[]) => {
      if (!document?.revisionId) return;
      queryClient.setQueryData(referenceListKey(document), (old: ReferenceDataValue[] | undefined) => updater(old ?? []));
    },
    [queryClient, document]
  );
};

export const useEditorCss = () => {
  const scope = useEditorScope();
  const { data } = useCss({
    documentSuperId: scope?.documentId ?? '',
    documentRevisionId: scope?.documentRevisionId,
    useDraft: scope?.useDraft,
    withStyle: scope?.withStyle as string | false | undefined,
    embedd: scope?.embedd,
  });
  return (data ?? {}) as { shadowedCss?: string; exposedCss?: string };
};

export const useSetEditorDocument = () => {
  const scope = useEditorScope();
  const queryClient = useQueryClient();

  return useCallback(
    (document: unknown) => {
      if (!scope) {
        queryClient.invalidateQueries({ queryKey: [...documentKeys.detailAll] });
        return;
      }
      queryClient.setQueryData(documentKeys.editorDocument(scope.documentId, scope.documentRevisionId, scope.useDraft, scope.withStyle), document);
    },
    [queryClient, scope]
  );
};
